import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import _last from 'lodash/last'
import _isEqual from 'lodash/isEqual'
import { mdiChevronDown } from '@mdi/js'
import { FormattedMessage, useIntl } from 'react-intl'
import _isEmpty from 'lodash/isEmpty'

import { useAppDispatch, useAppSelector, useDomainQueryParams } from 'app/hooks'
import {
  userHasManyTopDomainsSelector,
  userTopDomainsSelector,
} from 'features/auth/permissions/selectors'
import { commonMessages } from 'app/i18n/messages'
import { CommonButton } from 'components/Button'
import { H4 } from 'components/Typography'

import {
  StyledBreadcrumbsMenu,
  StyledBreadcrumbsMenuBody,
  StyledBreadcrumbsMenuFooter,
  StyledBreadcrumbsMenuHeader,
  StyledBreadcrumbsNav,
  StyledBreadcrumbsPath,
  StyledBreadcrumbsPathIcon,
  StyledBreadcrumbsPathTitle,
  StyledDesktopBreadcrumbs,
  StyledMobileBreadcrumbs,
} from './styles'
import DomainsSelectorDropdown from './DomainsSelectorDrodown'
import {
  domainToDomainDataCacheSelector,
  domainToSubDomainsCacheSelector,
  selectedNavDomainSelector,
  selectedNavTopDomainIdSelector,
} from 'features/domains/selectors'
import {
  getDomainNamesFromParent,
  mapDomainsToDropdownOptions,
  mapDomainToDropdownOption,
} from './utils'
import { getDomainSubDomainsThunk } from 'components/DomainBreadcrumbs/slice'
import {
  selectedNavDomainActions,
  selectedNavTopDomainActions,
} from 'features/domains/slice'
import DomainBreadcrumbsLoader from 'components/DomainBreadcrumbs/DomainBreadcrumbsLoader'
import { createDomainQueryParams } from 'features/domains/utils'

import { domainsSelectorMessages } from './messages'
import DomainsNavToRoot from './DomainsNavToRoot'

export type SelectedDomainIdsShape = string[]

// TODO: this component needs refactoring because of its high complexity
// TopDomain - is the first user domain in the tree(top domain of user tree branch),
// user can have several topDomains when he was added to several tree branches
// if user is in root domain his topDomain will be root domain
function DomainsSelector() {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const location = useLocation()
  const { pathname } = location
  const { formatMessage } = useIntl()
  const domainQueryParams = useDomainQueryParams()

  const [isLoading, setIsLoading] = useState(true)

  const [showMobileMenu, setShowMobileMenu] = useState(false)

  const handleMobileApply = () => setShowMobileMenu(false)

  const userTopDomains = useAppSelector(userTopDomainsSelector)
  const userHasManyTopDomains = useAppSelector(userHasManyTopDomainsSelector)

  const subDomainsCache = useAppSelector(domainToSubDomainsCacheSelector)
  const domainIdToDomainDataCache = useAppSelector(
    domainToDomainDataCacheSelector
  )
  const selectedNavDomain = useAppSelector(selectedNavDomainSelector.data)
  const selectedNavTopDomainId = useAppSelector(selectedNavTopDomainIdSelector)

  const [selectedDomainIds, setSelectedDomainIds] =
    useState<SelectedDomainIdsShape>(useDomainQueryParams())

  const selectedNavTopDomainIdRef = useRef<string | null>(null)
  // When selectedNavTopDomainId changes (and previous was not null) - clear selectedDomainIds
  useEffect(() => {
    if (selectedNavTopDomainIdRef.current !== selectedNavTopDomainId) {
      if (userHasManyTopDomains) {
        // adding default top domain to selectedDomainsIds
        setSelectedDomainIds([selectedNavTopDomainId])
        // when user has many top domains we always have a default one and this adds it to url params if it was missed
        if (_isEmpty(domainQueryParams)) {
          const domainQueryParams = createDomainQueryParams([
            selectedNavTopDomainId,
          ])
          history.replace(`${pathname}?${domainQueryParams}`)
        }
      } else {
        setSelectedDomainIds([])
      }
    }
    selectedNavTopDomainIdRef.current = selectedNavTopDomainId
  }, [
    userHasManyTopDomains,
    selectedNavTopDomainId,
    pathname,
    history,
    domainQueryParams,
  ])

  // putting url domain params to SelectedDomainIds state
  // for example if user visits url with domain params directly
  let queryParamsRef = useRef<null | string[]>([])
  useEffect(() => {
    if (!_isEqual(domainQueryParams, queryParamsRef.current)) {
      setSelectedDomainIds(domainQueryParams)
    }
    queryParamsRef.current = domainQueryParams
  }, [domainQueryParams])

  const mapDomainIdToDomainName = useMemo(
    () => (domainId: string) => {
      return domainIdToDomainDataCache[domainId]?.name
    },
    [domainIdToDomainDataCache]
  )

  const domainsCurrentPath = useMemo((): null | string => {
    const { length } = selectedDomainIds

    if (length === 1) {
      return selectedDomainIds.map(mapDomainIdToDomainName).toString()
    } else if (length > 1 && selectedNavDomain) {
      return getDomainNamesFromParent(selectedNavDomain).join(' / ')
    }
    return null
  }, [mapDomainIdToDomainName, selectedDomainIds, selectedNavDomain])

  const topSubDomainFromCache = subDomainsCache[selectedNavTopDomainId || '']

  const dropdownOptionsForTopDomains = useMemo(
    () => mapDomainsToDropdownOptions(userTopDomains || []),
    [userTopDomains]
  )

  const dropdownOptions = useMemo(
    () => topSubDomainFromCache?.map(mapDomainToDropdownOption) || [],
    [topSubDomainFromCache]
  )

  useEffect(() => {
    // When current selectedNavTopDomainId changes - fetch it's subDomains
    async function getTopDomainSubDomains() {
      if (selectedNavTopDomainId && !topSubDomainFromCache) {
        await dispatch(
          getDomainSubDomainsThunk({ domainId: selectedNavTopDomainId })
        )
      }
      setIsLoading(false)
    }
    getTopDomainSubDomains()
  }, [
    selectedNavTopDomainId,
    dispatch,
    domainQueryParams,
    topSubDomainFromCache,
  ])

  const selectDomainHandler = async (
    dropdownIndex: number,
    newDomainId: string
  ) => {
    if (dropdownIndex === 0) {
      // First dropdown with root/top subdomains/subgroups was selected
      const newSelectedNavDomain = userTopDomains.find(
        (domain) => domain.id === newDomainId
      )
      if (newSelectedNavDomain) {
        dispatch(
          selectedNavTopDomainActions.setNavTopDomain(newSelectedNavDomain)
        )
      }
      const domainQueryParams = createDomainQueryParams([newDomainId])
      history.push(`${pathname}?${domainQueryParams}`)
    } else {
      // Some next dropdown with nested domain/group was selected
      if (selectedDomainIds.length < dropdownIndex) {
        // new nested domain was selected. selectedDomainIds length gets +1
        const domainQueryParams = createDomainQueryParams([
          ...selectedDomainIds,
          newDomainId,
        ])
        history.push(`${pathname}?${domainQueryParams}`)
      } else {
        // Some previous dropdown was changed/selected
        if (selectedDomainIds.length > dropdownIndex) {
          let newSelectedDomainIds = [...selectedDomainIds]
          newSelectedDomainIds = newSelectedDomainIds.slice(0, dropdownIndex)
          newSelectedDomainIds[dropdownIndex] = newDomainId
          const domainQueryParams =
            createDomainQueryParams(newSelectedDomainIds)
          history.push(`${pathname}?${domainQueryParams}`)
        } else {
          // Change by index and current selectedDomainIds length depending on dropdownIndex
          const newSelectedDomainIds = [...selectedDomainIds]
          newSelectedDomainIds[dropdownIndex] = newDomainId
          const domainQueryParams =
            createDomainQueryParams(newSelectedDomainIds)
          history.push(`${pathname}?${domainQueryParams}`)
        }
      }
    }
  }

  // Get subdomains/subgroups when selectedDomainIds changes
  useEffect(() => {
    const getSubDomainInfo = async () => {
      const lastDomainIdInSelectedDomains = selectedDomainIds.length
        ? _last(selectedDomainIds)
        : selectedNavTopDomainId
      if (lastDomainIdInSelectedDomains) {
        const subDomainsResult = await dispatch(
          getDomainSubDomainsThunk({
            domainId: lastDomainIdInSelectedDomains,
          })
        )
        const res = await subDomainsResult
        if (Array.isArray(res.payload)) {
          dispatch(selectedNavDomainActions.succeed(res.payload[2]))
        }
        return subDomainsResult
      }
    }
    getSubDomainInfo()
  }, [selectedNavTopDomainId, dispatch, selectedDomainIds])

  if (isLoading) {
    return <DomainBreadcrumbsLoader />
  }

  return (
    <>
      <StyledBreadcrumbsNav>
        <StyledDesktopBreadcrumbs>
          {userHasManyTopDomains ? (
            <DomainsSelectorDropdown
              key={selectedDomainIds[0]}
              handleChange={selectDomainHandler}
              options={dropdownOptionsForTopDomains}
              dropdownIndex={0}
              defaultValue={selectedDomainIds[0]}
            />
          ) : (
            <>
              <DomainsNavToRoot isDisabled={!domainQueryParams.length} />
              <DomainsSelectorDropdown
                key={selectedDomainIds[0]}
                handleChange={selectDomainHandler}
                options={dropdownOptions}
                dropdownIndex={0}
                defaultValue={selectedDomainIds[0]}
              />
            </>
          )}
          {selectedDomainIds.map(
            (domainId, idx) =>
              !!subDomainsCache[selectedDomainIds[idx]]?.length && (
                <DomainsSelectorDropdown
                  key={`${idx + 1}_${domainId}`}
                  handleChange={selectDomainHandler}
                  options={subDomainsCache[selectedDomainIds[idx]].map(
                    mapDomainToDropdownOption
                  )}
                  dropdownIndex={idx + 1}
                  defaultValue={selectedDomainIds[idx + 1]}
                />
              )
          )}
        </StyledDesktopBreadcrumbs>
        <StyledMobileBreadcrumbs>
          {!userHasManyTopDomains && (
            <DomainsNavToRoot isDisabled={!domainQueryParams.length} />
          )}
          {
            <StyledBreadcrumbsPath
              onClick={() => {
                setShowMobileMenu(!showMobileMenu)
              }}
            >
              <StyledBreadcrumbsPathTitle>
                {domainsCurrentPath ||
                  formatMessage(domainsSelectorMessages.selectDomain)}
              </StyledBreadcrumbsPathTitle>
              <StyledBreadcrumbsPathIcon path={mdiChevronDown} size={1} />
            </StyledBreadcrumbsPath>
          }
          {showMobileMenu && (
            <StyledBreadcrumbsMenu>
              <StyledBreadcrumbsMenuHeader>
                <H4>
                  <FormattedMessage {...domainsSelectorMessages.selectDomain} />
                </H4>
              </StyledBreadcrumbsMenuHeader>
              <StyledBreadcrumbsMenuBody>
                {userHasManyTopDomains ? (
                  <DomainsSelectorDropdown
                    key={selectedDomainIds[0]}
                    handleChange={selectDomainHandler}
                    options={dropdownOptionsForTopDomains}
                    dropdownIndex={0}
                    defaultValue={selectedDomainIds[0]}
                  />
                ) : (
                  <DomainsSelectorDropdown
                    key={selectedDomainIds[0]}
                    handleChange={selectDomainHandler}
                    options={dropdownOptions}
                    dropdownIndex={0}
                    defaultValue={selectedDomainIds[0]}
                  />
                )}
                {selectedDomainIds.map(
                  (domainId, idx) =>
                    !!subDomainsCache[selectedDomainIds[idx]]?.length && (
                      <DomainsSelectorDropdown
                        key={`${idx + 1}_${domainId}`}
                        handleChange={selectDomainHandler}
                        options={subDomainsCache[selectedDomainIds[idx]].map(
                          mapDomainToDropdownOption
                        )}
                        dropdownIndex={idx + 1}
                        defaultValue={selectedDomainIds[idx + 1]}
                      />
                    )
                )}
              </StyledBreadcrumbsMenuBody>
              <StyledBreadcrumbsMenuFooter>
                <CommonButton onClick={handleMobileApply} variant="secondary">
                  <FormattedMessage {...commonMessages.cancel} />
                </CommonButton>
                <CommonButton onClick={handleMobileApply} variant="primary">
                  <FormattedMessage {...commonMessages.apply} />
                </CommonButton>
              </StyledBreadcrumbsMenuFooter>
            </StyledBreadcrumbsMenu>
          )}
        </StyledMobileBreadcrumbs>
      </StyledBreadcrumbsNav>
    </>
  )
}

export default DomainsSelector
